import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, ArrowRight, MapPin, User, Leaf, ShoppingCart, Info, Citrus, Sun, Droplets } from 'lucide-react';

const CitrusCatalogue = () => {
  const [selectedVariety, setSelectedVariety] = useState(null);

  const citrusVarieties = [
    {
      id: 'nagpur-orange',
      name: 'Nagpur Orange',
      subtitle: 'GI Tagged Santra',
      image: 'https://images.pexels.com/photos/1937743/pexels-photo-1937743.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹60-90',
      unit: 'per kg',
      minOrder: '200 kg',
      season: 'November - February',
      origin: 'Nagpur, Maharashtra',
      giTagged: true,
      organic: false,
      grade: 'GI Tagged Premium',
      juiceContent: '45-50%',
      weight: '150-250g per piece',
      description: 'The famous Nagpur Orange (Santra) is GI-tagged and known worldwide for its unique sweet-tangy taste and easy-to-peel skin. India\'s most beloved citrus fruit.',
      features: ['GI Tagged', 'Easy to peel', 'High juice content', 'Sweet-tangy', 'Vitamin C rich', 'Export quality'],
      farmer: {
        name: 'Prakash Meshram',
        location: 'Nagpur, Maharashtra',
        image: 'https://images.pexels.com/photos/2804327/pexels-photo-2804327.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '30 years',
        farmSize: '60 acres',
        story: 'Nagpur has been called the Orange City of India. Our family has been growing Santra here for three generations. The unique climate of Vidarbha gives our oranges their distinctive flavor.'
      },
      certifications: ['GI Tagged', 'APEDA Certified', 'FSSAI'],
      exportMarkets: ['Bangladesh', 'Nepal', 'UAE', 'Saudi Arabia']
    },
    {
      id: 'kinnow',
      name: 'Kinnow Mandarin',
      subtitle: 'Punjab\'s Pride - High Juice',
      image: 'https://images.pexels.com/photos/1435735/pexels-photo-1435735.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹40-60',
      unit: 'per kg',
      minOrder: '500 kg',
      season: 'December - February',
      origin: 'Abohar, Punjab',
      giTagged: false,
      organic: true,
      grade: 'Export Grade',
      juiceContent: '50-55%',
      weight: '120-180g per piece',
      description: 'Kinnow is a high-yield mandarin hybrid grown extensively in Punjab and Rajasthan. Known for exceptional juiciness, it\'s India\'s top citrus export and the backbone of the juice industry.',
      features: ['Very juicy', 'High yield', 'Processing grade', 'Good storage', 'Juice industry favorite'],
      farmer: {
        name: 'Harpreet Singh',
        location: 'Abohar, Punjab',
        image: 'https://images.pexels.com/photos/5717546/pexels-photo-5717546.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '25 years',
        farmSize: '80 acres',
        story: 'Punjab produces over 70% of India\'s Kinnow. Our orchards in Abohar benefit from the Sutlej river waters and the cold winter nights that make our fruit exceptionally sweet.'
      },
      certifications: ['Organic India', 'APEDA', 'GlobalGAP'],
      exportMarkets: ['Russia', 'UAE', 'Bangladesh', 'Sri Lanka', 'Nepal']
    },
    {
      id: 'sweet-lime',
      name: 'Sweet Lime (Mosambi)',
      subtitle: 'Year-round Fresh Juice',
      image: 'https://images.pexels.com/photos/997725/pexels-photo-997725.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹35-50',
      unit: 'per kg',
      minOrder: '300 kg',
      season: 'Year-round',
      origin: 'Andhra Pradesh',
      giTagged: false,
      organic: false,
      grade: 'Grade A',
      juiceContent: '40-45%',
      weight: '120-200g per piece',
      description: 'Sweet Lime or Mosambi is India\'s most popular juice fruit. Mild, sweet flavor with low acidity makes it suitable for all ages. Available throughout the year.',
      features: ['Low acidity', 'Mild sweet', 'Year-round availability', 'Juice grade', 'Easy to digest'],
      farmer: {
        name: 'Ravi Kumar Reddy',
        location: 'Nellore, Andhra Pradesh',
        image: 'https://images.pexels.com/photos/5704849/pexels-photo-5704849.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '20 years',
        farmSize: '45 acres',
        story: 'Sweet lime thrives in our coastal climate. We supply to juice vendors across South India and our fruit is known for its consistent sweetness and juice content.'
      },
      certifications: ['FSSAI', 'APEDA'],
      exportMarkets: ['UAE', 'Saudi Arabia']
    },
    {
      id: 'lemon',
      name: 'Kagzi Lemon',
      subtitle: 'Premium Indian Lemon',
      image: 'https://images.pexels.com/photos/1414110/pexels-photo-1414110.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹80-120',
      unit: 'per kg',
      minOrder: '100 kg',
      season: 'Year-round (Peak: Summer)',
      origin: 'Vidarbha, Maharashtra',
      giTagged: false,
      organic: true,
      grade: 'Premium',
      juiceContent: '35-40%',
      weight: '40-80g per piece',
      description: 'Kagzi Nimbu is the authentic Indian lemon known for thin skin and high juice content. Essential for Indian cuisine, pickles, and beverages. Strong citrus aroma.',
      features: ['Thin skin', 'High juice', 'Strong aroma', 'Pickle grade', 'Culinary essential'],
      farmer: {
        name: 'Ganesh Wankhede',
        location: 'Akola, Maharashtra',
        image: 'https://images.pexels.com/photos/5717628/pexels-photo-5717628.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '22 years',
        farmSize: '25 acres',
        story: 'Vidarbha region produces the best Kagzi lemons in India. The hot, dry climate concentrates the flavors and gives our lemons their distinctive punch.'
      },
      certifications: ['Organic Certified', 'FSSAI'],
      exportMarkets: ['UAE', 'UK']
    },
    {
      id: 'coorg-orange',
      name: 'Coorg Orange',
      subtitle: 'Hill Station Specialty',
      image: 'https://images.pexels.com/photos/2469555/pexels-photo-2469555.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹70-100',
      unit: 'per kg',
      minOrder: '150 kg',
      season: 'October - January',
      origin: 'Coorg, Karnataka',
      giTagged: true,
      organic: true,
      grade: 'GI Tagged Premium',
      juiceContent: '48-52%',
      weight: '100-150g per piece',
      description: 'Coorg Orange is a unique mandarin variety from the misty hills of Kodagu. GI-tagged for its distinctive flavor profile - sweeter than Nagpur orange with a hint of honey.',
      features: ['GI Tagged', 'Honey-sweet', 'Hill grown', 'Organic', 'Limited availability', 'Premium'],
      farmer: {
        name: 'Kaveri Muthanna',
        location: 'Madikeri, Coorg',
        image: 'https://images.pexels.com/photos/5704720/pexels-photo-5704720.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '18 years',
        farmSize: '15 acres',
        story: 'Our orange groves are nestled between coffee plantations in the Western Ghats. The mist, altitude, and organic practices give Coorg oranges their unique honey-like sweetness.'
      },
      certifications: ['GI Tagged', 'Organic India', 'Rainforest Alliance'],
      exportMarkets: ['Singapore', 'Malaysia']
    },
    {
      id: 'grapefruit',
      name: 'Indian Grapefruit',
      subtitle: 'Premium Pomelo & Grapefruit',
      image: 'https://images.pexels.com/photos/2128569/pexels-photo-2128569.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹100-150',
      unit: 'per kg',
      minOrder: '100 kg',
      season: 'November - March',
      origin: 'Tamil Nadu',
      giTagged: false,
      organic: false,
      grade: 'Premium',
      juiceContent: '38-42%',
      weight: '300-600g per piece',
      description: 'Indian grapefruit and pomelo varieties offer a unique bitter-sweet taste profile. Rich in antioxidants and perfect for health-conscious consumers and gourmet applications.',
      features: ['Bitter-sweet', 'Large size', 'Health focused', 'Gourmet grade', 'Antioxidant rich'],
      farmer: {
        name: 'Senthil Kumar',
        location: 'Tirunelveli, Tamil Nadu',
        image: 'https://images.pexels.com/photos/2382665/pexels-photo-2382665.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '15 years',
        farmSize: '20 acres',
        story: 'Grapefruit cultivation is still niche in India, but demand is growing rapidly. We cater to premium hotels, juice bars, and health food stores.'
      },
      certifications: ['FSSAI', 'APEDA'],
      exportMarkets: ['UAE', 'Singapore']
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1937743/pexels-photo-1937743.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Citrus orchards"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#263776]/95 via-[#263776]/70 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <Link to="/products/orivana-fresh" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to ORIVANA Fresh
          </Link>
          <Badge className="bg-[#F47D20] text-white mb-4 block w-fit">
            <Sun className="w-3 h-3 mr-1 inline" /> Vitamin C Powerhouse
          </Badge>
          <h1 className="text-5xl font-bold text-white mb-4">Citrus Fruits</h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Premium Indian citrus - Nagpur Orange, Kinnow, Sweet Lime, Lemons. GI-tagged varieties from India's finest orchards.
          </p>
        </div>
      </section>

      {/* Benefits Banner */}
      <section className="bg-gradient-to-r from-orange-500 to-yellow-500 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-8 text-white text-sm flex-wrap">
            <span className="flex items-center"><Sun className="w-4 h-4 mr-2" /> Rich in Vitamin C</span>
            <span>|</span>
            <span className="flex items-center"><Droplets className="w-4 h-4 mr-2" /> High Juice Content</span>
            <span>|</span>
            <span>GI Tagged Varieties</span>
            <span>|</span>
            <span>Year-round Supply</span>
          </div>
        </div>
      </section>

      {/* Varieties Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Varieties List */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-[#263776] mb-6">Select Variety</h2>
              <div className="space-y-4">
                {citrusVarieties.map((variety) => (
                  <Card 
                    key={variety.id}
                    className={`cursor-pointer transition-all duration-300 ${
                      selectedVariety?.id === variety.id 
                        ? 'ring-2 ring-[#F47D20] shadow-lg' 
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => setSelectedVariety(variety)}
                    data-testid={`citrus-variety-${variety.id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <img 
                        src={variety.image} 
                        alt={variety.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-[#263776]">{variety.name}</h3>
                          {variety.giTagged && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">GI</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{variety.origin}</p>
                        <p className="text-[#F47D20] font-bold mt-1">{variety.price} <span className="text-xs text-slate-400">{variety.unit}</span></p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Right: Selected Variety Details */}
            <div className="lg:col-span-2">
              {selectedVariety ? (
                <div className="sticky top-4">
                  <div className="relative rounded-xl overflow-hidden h-[350px] mb-6">
                    <img 
                      src={selectedVariety.image} 
                      alt={selectedVariety.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                      <Badge className="bg-[#263776] text-white">{selectedVariety.grade}</Badge>
                      {selectedVariety.giTagged && (
                        <Badge className="bg-amber-500 text-white">GI Tagged</Badge>
                      )}
                      {selectedVariety.organic && (
                        <Badge className="bg-green-600 text-white">
                          <Leaf className="w-3 h-3 mr-1" /> Organic
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h2 className="text-3xl font-bold text-[#263776] mb-2">{selectedVariety.name}</h2>
                      <p className="text-lg text-[#F47D20] font-medium mb-4">{selectedVariety.subtitle}</p>
                      <p className="text-slate-600 mb-4">{selectedVariety.description}</p>
                      
                      <div className="bg-slate-50 rounded-lg p-4 mb-4">
                        <h4 className="font-bold text-[#263776] mb-3 flex items-center">
                          <Info className="w-4 h-4 mr-2" /> Specifications
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-slate-500">Season:</span> <span className="font-medium">{selectedVariety.season}</span></div>
                          <div><span className="text-slate-500">Origin:</span> <span className="font-medium">{selectedVariety.origin}</span></div>
                          <div><span className="text-slate-500">Juice Content:</span> <span className="font-medium">{selectedVariety.juiceContent}</span></div>
                          <div><span className="text-slate-500">Weight:</span> <span className="font-medium">{selectedVariety.weight}</span></div>
                          <div><span className="text-slate-500">Min Order:</span> <span className="font-medium">{selectedVariety.minOrder}</span></div>
                          <div><span className="text-slate-500">Grade:</span> <span className="font-medium">{selectedVariety.grade}</span></div>
                        </div>
                      </div>

                      {selectedVariety.exportMarkets.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-bold text-[#263776] mb-2">Export Markets</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedVariety.exportMarkets.map((market, idx) => (
                              <Badge key={idx} className="bg-orange-100 text-orange-700">
                                {market}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mb-4">
                        <h4 className="font-bold text-[#263776] mb-2">Key Features</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedVariety.features.map((feature, idx) => (
                            <Badge key={idx} variant="outline" className="border-[#263776]/30">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-bold text-[#263776] mb-2">Certifications</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedVariety.certifications.map((cert, idx) => (
                            <Badge key={idx} className="bg-[#263776]/10 text-[#263776]">
                              {cert}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <Card className="mb-6 border-2 border-[#F47D20]/20">
                        <CardContent className="p-6">
                          <div className="text-3xl font-bold text-[#F47D20] mb-1">{selectedVariety.price}</div>
                          <div className="text-slate-500 mb-4">{selectedVariety.unit} (Wholesale)</div>
                          <div className="text-sm text-slate-600 mb-4">
                            Minimum Order: <span className="font-bold">{selectedVariety.minOrder}</span>
                          </div>
                          <Button asChild className="w-full bg-[#F47D20] hover:bg-[#d66b1a] mb-3">
                            <Link to={`/products/orivana-fresh/enquiry?product=${selectedVariety.id}&category=citrus`}>
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              Request Quote
                            </Link>
                          </Button>
                          <Button asChild variant="outline" className="w-full border-[#263776] text-[#263776]">
                            <Link to="/contact">
                              Contact Sales Team
                            </Link>
                          </Button>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-[#263776]/5 to-[#F47D20]/5">
                        <CardContent className="p-6">
                          <h4 className="font-bold text-[#263776] mb-4 flex items-center">
                            <User className="w-4 h-4 mr-2" /> Meet the Farmer
                          </h4>
                          <div className="flex items-start gap-4">
                            <img 
                              src={selectedVariety.farmer.image}
                              alt={selectedVariety.farmer.name}
                              className="w-20 h-20 rounded-full object-cover"
                            />
                            <div>
                              <h5 className="font-bold text-[#263776]">{selectedVariety.farmer.name}</h5>
                              <div className="flex items-center text-sm text-slate-500 mb-2">
                                <MapPin className="w-3 h-3 mr-1" />
                                {selectedVariety.farmer.location}
                              </div>
                              <div className="text-xs text-slate-400 mb-2">
                                {selectedVariety.farmer.experience} experience • {selectedVariety.farmer.farmSize}
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 mt-4 italic">
                            "{selectedVariety.farmer.story}"
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[500px] bg-slate-50 rounded-xl">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Sun className="w-8 h-8 text-orange-500" />
                    </div>
                    <h3 className="text-xl font-bold text-[#263776] mb-2">Select a Variety</h3>
                    <p className="text-slate-500">Click on a citrus variety to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Juice Industry Section */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-orange-100 text-orange-700 mb-4">FOR JUICE INDUSTRY</Badge>
              <h2 className="text-3xl font-bold text-[#263776] mb-4">Processing Grade Citrus</h2>
              <p className="text-slate-600 mb-6">
                We supply bulk quantities of processing-grade citrus fruits to juice manufacturers, concentrate producers, and essential oil extractors.
              </p>
              <ul className="space-y-3">
                {[
                  'Consistent quality & Brix levels',
                  'Large volume supply capability',
                  'Cold storage & handling',
                  'Seasonal contracts available',
                  'FSSAI & export documentation'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center text-slate-700">
                    <Droplets className="w-5 h-5 text-orange-500 mr-3" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-6 text-center bg-white">
                <div className="text-4xl font-bold text-orange-500 mb-2">500+</div>
                <div className="text-sm text-slate-600">Tonnes/Month Capacity</div>
              </Card>
              <Card className="p-6 text-center bg-white">
                <div className="text-4xl font-bold text-orange-500 mb-2">50%</div>
                <div className="text-sm text-slate-600">Avg Juice Content</div>
              </Card>
              <Card className="p-6 text-center bg-white">
                <div className="text-4xl font-bold text-orange-500 mb-2">12+</div>
                <div className="text-sm text-slate-600">Brix Average</div>
              </Card>
              <Card className="p-6 text-center bg-white">
                <div className="text-4xl font-bold text-orange-500 mb-2">365</div>
                <div className="text-sm text-slate-600">Days Availability</div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-[#263776]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Need Bulk Citrus Supply?</h2>
          <p className="text-white/80 mb-6">From table fruit to processing grade - we serve all segments of the citrus market</p>
          <Button asChild size="lg" className="bg-[#F47D20] hover:bg-[#d66b1a]">
            <Link to="/products/orivana-fresh/enquiry">Get Seasonal Quote</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default CitrusCatalogue;
